import * as React from 'react';
import { formatDate } from '../utils';
import { ActionRequest } from '../actions';
import { connect } from 'react-redux';
import { BaseElement, BasePlayerContainer, DatePicker, Table, TextSelect } from '../components';
import { ContainerContext, mapProps } from './index';
import { useNavigate, useParams } from 'react-router';
import { StateMap } from '../reducers';
import { QueryResult } from 'material-table';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FormControl, FormControlLabel, Grid2 as Grid, InputLabel, Radio, RadioGroup } from '@mui/material';
import moment, { Moment } from 'moment';

interface Props extends ContainerContext.Props {
    readonly playerId: string;
}

type CurrencyDirection = 'Ledger'|'Earned'|'Spent';

interface State
{
    readonly transactionType: string;
    readonly currencyDirection: CurrencyDirection;
    readonly startDate: Moment|null;
    readonly endDate: Moment|null;
}

class Container extends BaseElement<Props, State> {
    state: Readonly<State> = {
        transactionType: 'purchases',
        currencyDirection: 'Ledger',
        startDate: moment().subtract(1, 'months'),
        endDate: moment(),
    };
    tableRef = React.createRef<any>();

    componentDidUpdate(_prevProps: Props, prevState: State) {
        if (
            this.state.transactionType !== prevState.transactionType
            || this.state.currencyDirection !== prevState.currencyDirection
            || this.state.startDate?.valueOf() !== prevState.startDate?.valueOf()
            || this.state.endDate?.valueOf() !== prevState.endDate?.valueOf()
        ) {
            const currentTable = this.tableRef.current;
            if (currentTable) {
                currentTable.onQueryChange();
            }
        }
    }
    protected renderContainer(): React.JSX.Element {
        const { playerId } = this.props;

        return (
            <BasePlayerContainer
                {...this.props}
                styled={this.props.styled}
                title = {"Transactions"}
                playerId = {playerId}
                showSubTabs = {true}
            >
                {this.renderContent()}
            </BasePlayerContainer>
        );
    }

    private readonly renderContent = () => {
        const setStartDate = (startDate: moment.Moment|null) => {this.setState({ startDate })};
        const setEndDate = (endDate: moment.Moment|null) => {this.setState({ endDate })};
        const columns = [
            {
                title: 'Timestamp',
                field: 'timestamp',
                render: (rowData: any) => formatDate(rowData.timestamp),
            },
            {
                title: 'Item Type',
                field: 'itemType',
            },
            {
                title: 'Item Name',
                field: 'itemName',
            },
            {
                title: 'Promo Name',
                field: 'promoName',
            },
            {
                title: 'Source',
                field: 'source',
            },
            {
                title: 'Sub-Source',
                field: 'subsource',
            },
            {
                title: 'Currency',
                field: 'currency',
            },
            {
                title: 'Item Quantity',
                field: 'itemQuantity',
            },
            {
                title: 'Transaction ID',
                field: 'transactionId',
            },
            {
                title: 'Debit',
                field: 'debit',
            },
            {
                title: 'Credit',
                field: 'credit',
            },
            {
                title: 'Currency Balance',
                field: 'currencyBalance',
            },
        ];

        return (<>
            <Grid container>
                <Grid size={4} padding={3}>
                    <TextSelect
                        label="Transaction Type"
                        value={this.state.transactionType}
                        onChange={(transactionType) => this.setState({ transactionType })}
                    >
                        <optgroup label="Purchases">
                            <option value="purchases" label="Purchases"/>
                        </optgroup>

                        <optgroup label="MainGame">
                            <option value="cards" label="Cards"/>
                            <option value="entries" label="Entries"/>
                            <option value="fashionitems" label="Fashion Items"/>
                            <option value="gachakeys" label="Gacha Keys"/>
                            <option value="gems" label="Gems"/>
                            <option value="lipstick" label="Lipstick"/>
                            <option value="rubadges" label="RuBadges"/>
                            <option value="rucolors" label="RuColors"/>
                            <option value="rudollars" label="RuDollas"/>
                            <option value="tb_store_votes" label="TootBoot Store Votes"/>
                            <option value="tb_toots" label="TootBoot Toots"/>
                            <option value="tb_voting_tickets" label="TootBoot Voting Tickets"/>
                            <option value="tickets" label="Tickets"/>
                            <option value="werkcoins" label="WerkCoins"/>
                        </optgroup>

                        <optgroup label="Event">
                            <option value="eventcards" label="Event Cards"/>
                            <option value="eventfashionitems" label="Event Fashion Items"/>
                            <option value="eventlipstick" label="Event Lipstick"/>
                            <option value="eventwerkcoins" label="Event WerkCoins"/>
                        </optgroup>
                    </TextSelect>
                </Grid>
                <Grid size={4} padding={3}>
                    <InputLabel>Direction</InputLabel>
                    <FormControl>
                        <RadioGroup
                            row
                            defaultValue={this.state.currencyDirection}
                            onChange={(event) => this.setState({currencyDirection: event.target.value as CurrencyDirection})}
                        >
                            <FormControlLabel
                                disabled={this.state.transactionType === 'purchases'}
                                value="Ledger"
                                label="Ledger"
                                control={ <Radio/> }
                            />
                            <FormControlLabel
                                disabled={this.state.transactionType === 'purchases'}
                                value="Earned"
                                label="Earned"
                                control={ <Radio/> }
                            />
                            <FormControlLabel
                                disabled={this.state.transactionType === 'purchases'}
                                value="Spent"
                                label="Spent"
                                control={ <Radio/> }
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>
                <Grid size={4} container>
                    <Grid size={6} padding={3}>
                        <DatePicker value={this.state.startDate ?? moment()} onChange={setStartDate} label='Start Date'></DatePicker>
                    </Grid>
                    <Grid size={6} padding={3}>
                        <DatePicker value={this.state.endDate ?? moment()} onChange={setEndDate} label='End Date'></DatePicker>
                    </Grid>
                </Grid>
            </Grid>

            <Table
                tableRef={this.tableRef}
                styled={this.props.styled}
                columns={columns}
                data={this.getData}
                totalIsUnknown={true}
                options={{
                    showTitle: false,
                    selection: false,
                    paging: true,
                    pageSize: 30,
                    pageSizeOptions: [5, 10, 30, 50, 100],
                    emptyRowsWhenPaging: false,
                    search: false,
                    sorting: false,
                    draggable: false,
                }}
            />
        </>);
    };

    private readonly getData = async (query: any): Promise<QueryResult<any>> => {
        const { playerId } = this.props;
        if (!playerId) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
        const { page, pageSize } = query;
        const { transactionType, currencyDirection, startDate, endDate } = this.state;
        const type = transactionType + (transactionType === 'purchases' ? '' : currencyDirection);
        const params = {
            type,
            limit: pageSize,
            offset: pageSize * page,
            filterDateStart: startDate?.valueOf(),
            filterDateEnd: endDate?.valueOf(),
        };
        const result = await ActionRequest.get<GetTransactionsResponse>(`player/${playerId}/transactions`, params);
        if (!result) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
        const data = result.transactions;

        return {
            data,
            page,
            totalCount: pageSize * page + data.length + 1,
        };
    }
}

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
    playerId: '',
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    const params = useParams();
    const playerId = params.playerId || '';
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate} playerId={playerId}/>);
};
export default connect(mapStateToProps)(AppContainer);

export interface GetTransactionsResponse {
    type: string;
    transactions: {
        timestamp: string;
        itemType: string;
        itemName: string;
        promoName: string|null;
        source: string;
        subsource: string;
        currency: string;
        itemQuantity: number;
        transactionId: string|null;
        debit: number;
        credit: number;
        currencyBalance: number;
    }[];
}
